import { non_existing_txhs } from "../Contexts/RunContext.js"
import { is_string, is_hex, scriptHex_to_address } from "../helpers/helpers.js"
import { getLog, isElectron } from "../helpers/helpers.js"
import TokkenrBlockchain from "./TokkenrBlockchain.js"
import WhatsOnChainCustom from "./WhatsOnChainCustom.js"

//for loading RUN through webpack externals 
const Run = require("runlib")
const fetch = require('node-fetch')
const retry = require("async-retry")
/* speed up consecutive function calls by caching the result of calls with identical inputs
beware by default it only takes into account the 1st param for the cache
otherwise check https://github.com/sindresorhus/p-memoize#cachekey */
// const pMemoize = require("p-memoize")
import pMemoize from 'p-memoize';
import pDebounce from "../helpers/pDebounce.js"
import { hasNestedProperty } from "./helpers.js"
const ExpiryMap = require("expiry-map")  // for time-based cache expiration
// const pTimeout = require('p-timeout')
const promiseAny = require('p-any')



const isDevMode = !isElectron()

// --- pending-spends tracking ---
// WhatsOnChain's /script/{scripthash}/unspent endpoint adds unconfirmed outputs
// but does NOT remove inputs spent by unconfirmed (mempool) transactions.
// This means the purse can select UTXOs that are actually already spent by a
// pending BSV or token send, causing txn-mempool-conflict rejections.
// We track our own pending spends (outpoints spent by txs we broadcast) and
// filter them out of utxos() results until the tx confirms (or TTL expires).
const PENDING_SPENDS_TTL_MS = 72 * 60 * 60 * 1000  // 72 hours
const PENDING_SPENDS_CACHE_KEY = "tokkenr://pending-spends"

// -----------------------------------

// TODO add handling for AggregateError from p-any if they all reject

function is_empty_obj(obj) {
	return obj // null and undefined check
		&& Object.keys(obj).length === 0
		&& obj.constructor === Object
}

/**
 * BSV nodes reject transactions with specific reason strings, and some of them actually
 * mean "this exact tx is ALREADY accepted by the network". Broadcasting is idempotent,
 * so those cases must be treated as a SUCCESS, not a failure.
 *
 * This matters a lot here: if a broadcast succeeds but we mis-report it as failed, the
 * user retries, the app rebuilds a NEW tx (new timestamp => new txid) spending the SAME
 * utxos, and every subsequent attempt then fails with txn-mempool-conflict forever.
 */
function is_already_in_mempool_reason(reason) {
	const r = String(reason || "").toLowerCase()
	return r.includes("already known")
		|| r.includes("txn-already-known")
		|| r.includes("already in the mempool")
		|| r.includes("txn-already-in-mempool")
}

/**
 * Deterministic rejections: re-sending the exact same rawtx can never change the outcome,
 * so retrying only wastes time and floods the logs.
 *  - 258 txn-mempool-conflict : another tx already spends these input(s)
 *  - missing-inputs           : the utxos are already spent or unknown to the node
 *  - bad-txns-*               : the tx itself is invalid
 */
function is_non_retryable_broadcast_reason(reason) {
	const r = String(reason || "").toLowerCase()
	return r.includes("txn-mempool-conflict")
		|| r.includes("missing-inputs")
		|| r.includes("bad-txns")
		|| is_already_in_mempool_reason(r)
}


/**
 * Note on caching: If you want to optimise this CustomBlockchain implementation,
 * set a cache:... to options passed in constructor
 * that way fetch will not refetch if it's in the cache already
 */
export default class CustomBlockchain extends Run.api.Blockchain {
	constructor(options, cache) {
		super()
		this.classname = "CustomBlockchain: "
		const log = getLog(this.classname)
		this.log = log

		this.DEBUG = true
		this.config = {
			use_memoize: true,
			use_retries: true,
			use_debounce: true,
		}
		log("this.config=", JSON.stringify(this.config))

		// for webpack-dev-server to work
		const basic_options = { ...options }
		if (basic_options.cache) delete basic_options.cache
		// console.log("basic_options 1 =", basic_options)
		function get_options_copy() {
			// console.log("basic_options 2 =", basic_options)
			return JSON.parse(JSON.stringify(basic_options))
		}

		if (cache) {
			this.cache = cache
			options.cache = cache
		}

		this.memoize_cache = new ExpiryMap(2 * 1000)  // cached values expire after X ms

		// to avoid the error "Blockchain mismatch" we must define .api, .apiKey & .network 
		// in the same way as we define them in the Run() object
		// const default_run_api = "run"    // deprecated since run_0.6.42
		const default_run_api = "whatsonchain"

		this.api = options.api || default_run_api

		this.retry_config = {
			retries: 2,  // 2 means 2 retries so 3 calls in all
			minTimeout: 500,  // in ms
			maxTimeout: 5000,  // in ms
			factor: 2,  // exponential-backoff factor
		}

		this.blockchains = []
		this.options = options

		//
		// TokkenR API
		//
		this.blockchain_tokkenr = new TokkenrBlockchain()
		this.blockchains.push(this.blockchain_tokkenr)

		//
		// RUN API (deprecated)
		//
		// var options_copy = get_options_copy()
		// if (cache) options_copy.cache = cache
		// options_copy.api = default_run_api
		// this.blockchain_runapi = (new Run(options_copy)).blockchain
		// this.blockchains.push(this.blockchain_runapi)

		//
		// WhatsOnChain API
		//
		var options_copy = get_options_copy()
		if (cache) options_copy.cache = cache
		options_copy.api = "whatsonchain"

		// this.blockchain_whatsonchain = (new Run(options_copy)).blockchain
		this.blockchain_whatsonchain = new WhatsOnChainCustom(options_copy)

		log("this.blockchain_whatsonchain:", this.blockchain_whatsonchain)
		this.blockchains.push(this.blockchain_whatsonchain)

		// allow safety check in logs
		this.log_all_caches()

		// pending-spends: outpoints spent by our own unconfirmed broadcasts.
		// lazily loaded from cache on first utxos()/broadcast() call.
		this._pending_spends = null  // Map: outpoint -> { outpoint, txid, time }
	}

	/**
	 * Sets the purse script so we know which inputs to track as pending spends.
	 * Called after update_run() when the purse is initialized.
	 */
	set_purse_script(script) {
		const log = getLog(this.classname, "set_purse_script():")
		this.purse_script = script
		log("purse_script =", script)
	}

	/**
	 * Lazily loads pending spends from the persistent cache (run.cache) into
	 * the in-memory Map. Prunes entries older than PENDING_SPENDS_TTL_MS.
	 * Safe to call multiple times — only loads once.
	 */
	async _ensure_pending_spends_loaded() {
		if (this._pending_spends !== null) return
		this._pending_spends = new Map()
		if (!this.cache) return
		try {
			const data = await this.cache.get(PENDING_SPENDS_CACHE_KEY)
			if (Array.isArray(data)) {
				const now = Date.now()
				for (const entry of data) {
					if (entry && entry.outpoint && entry.time
						&& (now - entry.time < PENDING_SPENDS_TTL_MS)) {
						this._pending_spends.set(entry.outpoint, entry)
					}
				}
			}
		} catch (e) {
			// non-critical: start with empty map if cache read fails
		}
	}

	/**
	 * Persists the pending-spends map to the cache (fire-and-forget).
	 */
	_save_pending_spends() {
		if (!this.cache || this._pending_spends === null) return
		try {
			const arr = Array.from(this._pending_spends.values())
			this.cache.set(PENDING_SPENDS_CACHE_KEY, arr)
		} catch (e) {
			// non-critical
		}
	}

	/**
	 * Records all input outpoints of a just-broadcast tx as pending spends.
	 * Also writes spend:// cache entries so the Run SDK's spends() is aware.
	 * Non-blocking, non-throwing — never lets a recording failure block a broadcast.
	 */
	async _record_pending_spends(bsvtx) {
		try {
			await this._ensure_pending_spends_loaded()
			const txid = bsvtx.hash
			const now = Date.now()
			let changed = false
			for (const input of bsvtx.inputs) {
				const outpoint = input.prevTxId.toString("hex") + "_o" + input.outputIndex
				if (!this._pending_spends.has(outpoint)) {
					this._pending_spends.set(outpoint, { outpoint, txid, time: now })
					changed = true
					// write spend:// cache entry so Run SDK's spends() knows immediately
					if (this.cache) {
						this.cache.set("spend://" + outpoint, txid)
					}
				}
			}
			if (changed) {
				this._save_pending_spends()
			}
		} catch (e) {
			// non-critical: never let recording failure block the broadcast
		}
	}

	/**
	 * Filters out pending-spent outpoints from a UTXO array.
	 * Also prunes TTL-expired entries (txs that were never confirmed and
	 * evicted from mempool — their inputs become spendable again).
	 * Synchronous — only reads the in-memory Map (call _ensure_pending_spends_loaded first).
	 */
	_filter_pending_spends(utxos) {
		if (!this._pending_spends || this._pending_spends.size === 0) return utxos
		const now = Date.now()
		let pruned = false
		const filtered = utxos.filter(u => {
			const outpoint = u.txid + "_o" + u.vout
			const entry = this._pending_spends.get(outpoint)
			if (entry) {
				if (now - entry.time >= PENDING_SPENDS_TTL_MS) {
					this._pending_spends.delete(outpoint)
					pruned = true
					return true  // TTL expired → input is spendable again
				}
				return false  // pending-spent → filter out
			}
			return true
		})
		if (pruned) this._save_pending_spends()
		return filtered
	}

	/**
	 * Evicts pending-spends entries for txs that have been confirmed.
	 * Called from _refresh_bsv_history() after fetching the address history
	 * (which includes block heights for confirmed txs).
	 */
	async evict_confirmed_spends(confirmed_txids) {
		try {
			await this._ensure_pending_spends_loaded()
			if (this._pending_spends.size === 0) return
			const txid_set = new Set(confirmed_txids)
			let changed = false
			for (const [outpoint, entry] of this._pending_spends) {
				if (txid_set.has(entry.txid)) {
					this._pending_spends.delete(outpoint)
					changed = true
				}
			}
			if (changed) {
				this._save_pending_spends()
			}
		} catch (e) {
			// non-critical
		}
	}

	/**
	 * this is very useful to check if all caches are actually the same in memory
	 * We had a LOT of errors just because we had different caches and it messed up everything!
	 * especially the bsv purse balance. (28.02.2022)
	 */
	log_all_caches() {
		const log = getLog(this.classname, "log_all_caches():")

		const all_caches = [this.cache, ...this.blockchains.map(e => e.cache)]
		let comparisons = []
		for (let i = 0; i < all_caches.length - 1; i++) {
			// '===' operator returns true only if same obj in memory
			comparisons.push(all_caches[i] === all_caches[i + 1])
		}
		log("are all caches the same pointer in memory ?", comparisons)
		if (isDevMode) log("all_caches=", all_caches)
	}

	get network() {
		if (this.blockchain_runapi)
			return this.blockchain_runapi.network
		return "main" // default
	}

	//
	// --- clear_cache() ---
	//
	/**
	 * clears the in-memory memoize_cache (fetch/time/spends results).
	 * call this right before a critical operation (ex: building & broadcasting a transaction)
	 * to force fresh lookups instead of relying on cached results that could be up to
	 * memoize_cache's TTL old.
	 * note: this does NOT cancel any currently in-flight (debounced/retrying) calls,
	 * it only discards already-resolved cached results so the NEXT call refetches.
	 */
	clear_cache() {
		const log = getLog(this.classname, "clear_cache():")
		log("clearing memoize_cache (had", this.memoize_cache.size, "entries)")
		this.memoize_cache.clear()
	}

	//
	// --- diagnostics helper ---
	//
	/**
	 * Re-inspects an array of already-settled call promises via Promise.allSettled(), to
	 * produce a clear, human-readable per-endpoint breakdown of success/failure reasons.
	 *
	 * Why: when ALL promises in a promiseAny() call reject, p-any only gives back a vague
	 * AggregateError, which is what produced unhelpful messages like
	 * "all blockchains (2) failed for broadcasting txid ..." with no clue WHY each one failed.
	 *
	 * Meant to be called ONLY from inside a catch block, AFTER promiseAny() has already
	 * rejected (meaning every promise in allpromises has already settled) - it will NOT
	 * re-trigger any network calls, since JS promises are immutable once settled.
	 *
	 * @param {Array<Promise>} allpromises
	 * @param {Array<string>} labels same length & order as allpromises, used to identify each entry
	 */
	async _describe_settled_results(allpromises, labels) {
		const settled = await Promise.allSettled(allpromises)
		return settled.map((result, i) => {
			const label = (labels && labels[i]) || ("endpoint[" + i + "]")
			if (result.status === "fulfilled") {
				return { endpoint: label, status: "fulfilled", value: result.value }
			} else {
				const reason = result.reason
				return { endpoint: label, status: "rejected", reason: (reason && reason.message) || String(reason) }
			}
		})
	}

	//
	// --- broadcast() ---
	//
	// we don't want to memoize or debounce broadcasts ! rather broadcast twice than once
	async broadcast(rawtx) {
		return await retry(async (bail) => {
			try {
				return await this._broadcast(rawtx)
			} catch (error) {
				// deterministic rejections (mempool conflict, missing inputs, invalid tx)
				// can NEVER succeed by re-sending the same rawtx => stop retrying immediately.
				if (error && error.non_retryable) {
					bail(error)
					return
				}
				throw error // transient (network/5xx/timeout) => let async-retry retry it
			}
		}, this.retry_config)
	}
	async _broadcast(rawtx) {
		const log = getLog(this.classname + "broadcast():")
		if (!is_string(rawtx)) throw new Error([
			"CustomBlockchain: broadcast(): must be string, got rawtx:[", typeof rawtx, "]=",
			JSON.stringify(rawtx)].join(' ')
		)

		const bsvtx = new bsv.Transaction(rawtx)
		const txhash = bsvtx.hash
		// if (this.DEBUG) log("DEBUG, txhash=", txhash, "rawtx=", rawtx)
		log("with txhash=", txhash, "rawtx=", rawtx)

		// our goal is to broadcast to ALL blockchains to increase reliability
		// and take the response from the FIRST one that fulfils it
		var allpromises = []
		var blockchain_labels = []
		this.blockchains.forEach(blockchain => {
			allpromises.push(blockchain.broadcast(rawtx))
			blockchain_labels.push(blockchain.api || blockchain.classname || "unknown_blockchain")
		})
		var res_any
		try {
			// broadcast to all & take the first that succeeds for speed
			log("broadcasting to", allpromises.length, "endpoints for txhash=", txhash)
			res_any = await promiseAny(allpromises)
		} catch (e) {
			// p-any's AggregateError swallows the individual per-blockchain rejection reasons,
			// so we re-inspect the (already settled) promises to get a clear breakdown of WHY
			// each blockchain rejected the broadcast, instead of a generic "all blockchains failed"
			const detailed_results = await this._describe_settled_results(allpromises, blockchain_labels)

			// If ANY endpoint tells us this exact tx is already known / already in the mempool,
			// then the network HAS accepted it: broadcasting is idempotent, so this is a SUCCESS.
			const already_accepted = detailed_results.find(r =>
				r.status === "rejected" && is_already_in_mempool_reason(r.reason))
			if (already_accepted) {
				log("txid", txhash, "is already in the mempool according to", already_accepted.endpoint,
					"=> treating this broadcast as SUCCESSFUL (broadcasting is idempotent)")
				this._record_pending_spends(bsvtx)  // fire-and-forget
				return String(txhash)
			}

			// a conflict means a DIFFERENT tx already spends these same utxo(s) — almost always
			// a previous attempt of this very send that actually made it to the mempool.
			const has_conflict = detailed_results.some(r =>
				r.status === "rejected" && String(r.reason || "").toLowerCase().includes("txn-mempool-conflict"))

			const error_mess = String([
				"CustomBlockchain: broadcast(): all blockchains (" + allpromises.length + ") failed for broadcasting txid",
				txhash,
				(has_conflict
					? "\n => txn-mempool-conflict: another transaction spending the SAME utxo(s) is already"
					+ " in the mempool (most likely a previous attempt of this same send that actually"
					+ " succeeded). Wait for it to confirm (or be evicted) before retrying."
					: ""),
				"\n per-blockchain results:", JSON.stringify(detailed_results, null, 2)
			].join(' '))
			log(error_mess, "rawtx=", rawtx)
			console.error(error_mess, "rawtx=", rawtx)

			const err = new Error(error_mess)
			// tells broadcast()'s retry wrapper whether retrying could ever help
			err.non_retryable = detailed_results.some(r =>
				r.status === "rejected" && is_non_retryable_broadcast_reason(r.reason))
			throw err
		}
	// returned value should be a hex string which is the txid
	if (!is_string(res_any) || res_any.length === 0 || !is_hex(res_any)) {
		throw new Error(["CustomBlockchain: broadcast(): res_any should be an hex",
			"string but got:", res_any].join(' '))
	}
	this._record_pending_spends(bsvtx)  // fire-and-forget: track spent outpoints
	return String(res_any) // we return the first successful one
	}


	check_non_existing_txhs(txid_or_txh) {
		try {
			const txh = txid_or_txh.split("_")[0]
			if (non_existing_txhs.includes(txh)) {
				throw new Error("CustomBlockchain: fetch(): non_existing_txh for " + txh)
			}
		} catch (e) {
			throw new Error("check_non_existing_txhs: " + e)
		}
	}

	//
	// --- fetch() ---
	//
	async fetch(txid, force = false) {
		if (!this.config.use_memoize)
			return await this._fetch_debounced(txid, force)

		// we must create it only once and then reutilize it for it to work
		if (!this.fetch_memoized) {
			this.fetch_memoized = pMemoize(this._fetch_debounced, { cache: this.memoize_cache }).bind(this)
		}
		return await this.fetch_memoized(txid, force)
	}
	async _fetch_debounced(txid, force = false) {
		if (!this.config.use_debounce)
			return await this._fetch_retry(txid, force)

		// we must create it only once and then reutilize it for it to work
		// but me scope it to only this key
		if (!this.fetch_debounced) this.fetch_debounced = {}
		if (!this.fetch_debounced[txid]) {
			// pDebounce.promise will return the first result for all those that have been called in parallel
			this.fetch_debounced[txid] = pDebounce.promise(
				async () => { return await this._fetch_retry(txid, force) }
			).bind(this)
		}
		return await this.fetch_debounced[txid]()
	}
	async _fetch_retry(txid, force = false) {
		if (!this.config.use_retries)
			return await this._fetch(txid, force)

		this.check_non_existing_txhs(txid)

		return await retry(async () => {
			return await this._fetch(txid, force = false)
		}, this.retry_config)
	}
	async _fetch(txid, force = false) {
		const log = getLog(this.classname, "fetch(" + txid + "):")

		if (this.DEBUG) log("force=", force)
		if (!is_string(txid)) {
			// we have rare cases where we get txid = [object]: {}
			throw new Error([
				"CustomBlockchain: fetch(): must be string, got txid = [", typeof txid, "]:",
				JSON.stringify(txid)
			].join(' '))
		}
		// try loading from the cache first
		if (this.cache) {
			const txh = txid.split('_')[0]
			const rawtx = await this.cache.get("tx://" + txh)
			if (rawtx) {
				if (this.DEBUG) log("found in cache: ", txh)
				return rawtx
			}
		}

		// is doing this a security risk? probably YES
		// unban the tx in all blockchains to make sure we will be able to fetch
		// await Promise.all(this.blockchains.map(async (blockchain) => {
		// 	await this.cache.set('ban://'+txid, false)
		// 	await blockchain.cache.set('ban://'+txid, false)
		// }))

		// our goal is to talk with ALL blockchains to increase reliability
		// and take the response from the FIRST one that fulfils it
		var allpromises = []
		this.blockchains.forEach(async (blockchain) => {
			allpromises.push(blockchain.fetch(txid, force))
		})
		var res_any
		try {
			// we get the first successful one
			res_any = await promiseAny(allpromises)
		} catch (e) {
			var response
			// TODO requires cors server ! => apparently not anymore ?
			var cors_server = "" // "https://zhell-cors-escape.herokuapp.com/"
			var test_url = cors_server + "https://api.whatsonchain.com/v1/bsv/main/tx/" + txid + "/hex"
			try {
				response = await fetch(test_url)
				response = await response.text()
			} catch (e2) {
				console.error("error fetching whatsonchain:", e2)
			}
			var error = ["CustomBlockchain: fetch(): all blockchains failed for", txid].join(' ')
			console.error(error, " vs response=", response)
			// throw new Error(error)
			throw new Error(e)
		}
		// returned value should be a hex string which is the rawtx
		if (!is_string(res_any) || res_any.length === 0 || !is_hex(res_any))
			throw new Error(["CustomBlockchain: fetch(): res_any should be an hex string (rawtx) but got:", res_any].join(' '))

		// if(this.DEBUG) log("promises res_any=",res_any) // very big outputs
		const rawtx = res_any

		// save to local cache & return
		if (this.cache) this.cache.set("tx://" + txid, rawtx) // async but we don't need to await here

		return rawtx
	}

	//
	// --- utxos() ---
	//
	/* BEWARE THE NODE DOES NOT INDEXES BSV ADDRESSES SO IF YOU TRY TO RETRIEVE
	UTXOS FROM A PURSE IT WILL ANSWER AN EMPTY ARRAY */
	async utxos(script, class_origin = null) {
		let res
		if (!this.config.use_memoize) {
			res = await this._utxos_debounced(script, class_origin)
		}
		else {
			// we must create it only once and then reutilize it for it to work
			if (!this.utxos_memoized) {
				this.utxos_memoized = pMemoize(this._utxos_debounced, {
					cache: this.memoize_cache,
					cacheKey: (script, class_origin) => { return script + ":::" + class_origin }
				}).bind(this)
			}
			res = await this.utxos_memoized(script, class_origin)
		}
		// apply pending-spends filter AFTER memoize cache: even if the memoized
		// result is stale (pre-broadcast), the filter removes pending-spent outpoints.
		await this._ensure_pending_spends_loaded()
		return this._filter_pending_spends(res)
	}
	async _utxos_debounced(script, class_origin = null) {
		if (!this.config.use_debounce)
			return await this._utxos_retry(script, class_origin)

		// we must create it only once and then reutilize it for it to work
		// but me scope it to only this key
		if (!this.utxos_debounced) this.utxos_debounced = {}
		if (!this.utxos_debounced[script]) {
			// pDebounce.promise will return the first result for all those that have been called in parallel
			this.utxos_debounced[script] = pDebounce.promise(
				async () => { return await this._utxos_retry(script, class_origin) }
			).bind(this)
		}
		return await this.utxos_debounced[script]()
	}
	async _utxos_retry(script, class_origin = null) {
		if (!this.config.use_retries)
			return await this._utxos(script, class_origin)

		// allow safety check in logs
		if (!this.utxos_call_counter) this.utxos_call_counter = 0
		if (this.utxos_call_counter % 100) { // log caches every 100 calls so that we can check in logs
			this.utxos_call_counter++
			this.log_all_caches()
		}

		return await retry(async () => {
			return await this._utxos(script, class_origin)
		}, this.retry_config)
	}
	async _utxos(script, class_origin = null) {
		const address = scriptHex_to_address(script)
		const function_id = [this.classname, "utxos(" + script + ", address=" + address
			+ ", class_origin=" + class_origin + "): "].join(" ")
		const log = getLog(function_id)

		if (this.DEBUG) log("with script=", script, "and class_origin=", class_origin)
		// our goal is to talk with ALL blockchains to increase reliability
		// and take the response from the FIRST one that fulfils it
		/* BEWARE THE TokkenR NODE DOES NOT INDEXES BSV ADDRESSES SO IF YOU TRY TO RETRIEVE
		UTXOS FROM A PURSE IT WILL ANSWER AN EMPTY ARRAY */
		var promises_not_tokkenr = []
		promises_not_tokkenr.push(this.blockchain_whatsonchain.utxos(script))
		// promises_not_tokkenr.push(this.blockchain_runapi.utxos(script)) // deprecated

		var res_any
		try {
			/* if the tokkenr node gives an answer that is a non-empty array, good we take that,
			it means that the request for utxos was probably a token address, otherwise if the answer
			is an empty array, it was probably a purse address so we ask other endpoints instead
			*/
			// class_origin = token address, no class_origin = bsv address (not indexed by tokkenr node for now)
			var res_tokkenr
			if (class_origin) {
				try {
					log("calling blockchain_tokkenr with script=", script, "and class_origin=", class_origin)
					res_tokkenr = await this.blockchain_tokkenr.utxos_with_class_origin(script, class_origin)
				} catch (e) {
					// TODO see if we really want to throw here ? tokkenr node should never be down
					// but what if it is ?
					throw new Error(function_id + "Error with res_tokkenr: " + e + ", res_tokkenr=" + res_tokkenr)
				}
			}

			if (res_tokkenr && res_tokkenr.length > 0) {
				res_any = res_tokkenr
			} else {
				// we return the first successful one with promiseAny
				res_any = await promiseAny(promises_not_tokkenr)
			}
		} catch (e) {
			const errormess = "CustomBlockchain: utxos(): all blockchains failed. " + e + " for script=" + script
			console.error(errormess)  // this is necessary in dev mode to get the full content instead of [object Array]
			throw new Error(errormess)
		}
		// returned value should be an array of utxos
		if (!Array.isArray(res_any))
			throw new Error("CustomBlockchain: utxos(): res_any should be an array but got: " + res_any + " for script=" + script)

		if (this.DEBUG) log("promises res_any= (type", typeof res_any, ", length", res_any.length, "): ", res_any)
		return res_any || []  // we need to return empty array if there is no utxos for that address
	}

	//
	// --- time() ---
	//
	/**
	 * Returns the block time the transaction was confirmed, or the mempool acceptance time if not yet in a block
	 * 
	 * @param {string} txid 
	 * @returns the timestamp in ms
	 */
	async time(txid) {
		if (!this.config.use_memoize)
			return await this._time_debounced(txid)

		// we must create it only once and then reutilize it for it to work
		if (!this.time_memoized) {
			this.time_memoized = pMemoize(this._time_debounced, { cache: this.memoize_cache }).bind(this)
		}
		return await this.time_memoized(txid)
	}
	async _time_debounced(txid) {
		if (!this.config.use_debounce)
			return await this._time_retry(txid)

		// we must create it only once and then reutilize it for it to work
		// but me scope it to only this key
		if (!this.time_debounced) this.time_debounced = {}
		if (!this.time_debounced[txid]) {
			// pDebounce.promise will return the first result for all those that have been called in parallel
			this.time_debounced[txid] = pDebounce.promise(
				async () => { return await this._time_retry(txid) }
			).bind(this)
		}
		return await this.time_debounced[txid]()
	}
	async _time_retry(txid) {
		if (!this.config.use_retries)
			return await this._time(txid)

		this.check_non_existing_txhs(txid)
		return await retry(async () => {
			return await this._time(txid)
		}, this.retry_config)
	}
	async _time(txid) {
		const log = getLog(this.classname, "time(" + txid + "):")

		if (this.DEBUG) log()
		if (!is_string(txid)) throw new Error(["CustomBlockchain: time(): must be string, got:", txid].join(' '))

		// try loading from the cache first
		if (this.cache) {
			let tx_time = await this.cache.get("time://" + txid)
			if (!tx_time) {
				const txhash = txid.split('_')[0]
				tx_time = await this.cache.get("time://" + txhash)
			}
			if (tx_time) {
				if (this.DEBUG) log("found in cache: ", tx_time)
				return tx_time
			}
		}
		// our goal is to talk with ALL blockchains to increase reliability
		// and take the response from the FIRST one that fulfils it
		var allpromises = []
		this.blockchains.forEach(blockchain => {
			allpromises.push(blockchain.time(txid))
		})
		var res_any
		try {
			res_any = await promiseAny(allpromises)
		} catch (e) {
			console.error("CustomBlockchain: time(): all blockchains failed for", txid)
			throw new Error(["CustomBlockchain: time(): all blockchains failed for", txid].join(' '))
		}
		// retrieved value should be an integer
		if (!Number.isInteger(res_any))
			throw new Error(["CustomBlockchain: time(): res_any should be an integer but got:", res_any].join(' '))
		if (this.DEBUG) log("promises res_any=", res_any)
		return parseInt(res_any) // we return the first successful one
	}

	//
	// --- spends() ---
	//
	async spends(txid, vout) {
		if (!this.config.use_memoize)
			return await this._spends_debounced(txid, vout)

		// we must create it only once and then reutilize it for it to work
		if (!this.spends_memoized) {
			this.spends_memoized = pMemoize(this._spends_debounced, {
				cache: this.memoize_cache,
				cacheKey: (txid, vout) => { return txid + "_o" + vout }
			}).bind(this)
		}
		return await this.spends_memoized(txid, vout)
	}
	async _spends_debounced(txid, vout) {
		if (!this.config.use_debounce)
			return await this._spends_retry(txid, vout)

		// we must create it only once and then reutilize it for it to work
		// but me scope it to only this key
		const key = txid + "_o" + vout
		if (!this.spends_debounced) this.spends_debounced = {}
		if (!this.spends_debounced[key]) {
			// pDebounce.promise will return the first result for all those that have been called in parallel
			this.spends_debounced[key] = pDebounce.promise(
				async () => { return await this._spends_retry(txid, vout) }
			).bind(this)
		}
		return await this.spends_debounced[key]()
	}
	async _spends_retry(txid, vout) {

		if (!this.config.use_retries)
			return await this._spends(txid, vout)

		this.check_non_existing_txhs(txid)

		return await retry(async () => {
			return await this._spends(txid, vout)
		}, this.retry_config)
	}
	async _spends(txid, vout) {
		const location = txid + "_o" + vout
		const log = getLog(this.classname, "spends(" + location + "):")

		if (this.DEBUG) log()
		if (!is_string(txid)) throw new Error(["CustomBlockchain: spends(): must be string, got:", txid].join(' '))

		// try loading from the cache first
		txid = txid.split('_')[0] // safety check
		let search_cache = "spend://" + txid + "_o" + vout
		if (this.cache) {
			const spend_txid = await this.cache.get(search_cache)
			if (spend_txid) {
				if (this.DEBUG) log("found in cache: ", spend_txid)
				return spend_txid
			}
			else {
				// // not found in cache often means it isn't spent yet
				// log("not found in cache: vout=",vout,"txid=",txid,
				// 	"\n=>",search_cache)
			}
		}
		// our goal is to talk with ALL blockchains to increase reliability
		// and take the response from the FIRST one that fulfils it
		//
		// BUT the 1-st one might say "no spend" and the second one will find one
		// because the 1st one is slower on syncing on their end.
		// SO to bypass that issue we WILL load a spend that we find later
		// so that another call to spends() or sync() will return the cached spend
		// and not return "no spend" again
		const get_spend_and_load_it = async (blockchain, txid, vout) => {
			var spend_txid = await blockchain.spends(txid, vout)
			if (this.cache && spend_txid) {
				if (this.DEBUG) log("adding to cache for", search_cache, ":", spend_txid)
				// no await here, we update cache asynchronously
				this.cache.set(search_cache, spend_txid)
			}
		}
		var allpromises = []
		var blockchain_labels = []
		this.blockchains.forEach(blockchain => {
			// whatsonchain api doesn't support spends for now
			if (hasNestedProperty(blockchain, "api") && blockchain.api == "whatsonchain") {
				// do nothing
			} else {
				//allpromises.push(blockchain.spends(txid, vout))
				allpromises.push(get_spend_and_load_it(blockchain, txid, vout))
				blockchain_labels.push(blockchain.api || blockchain.classname || "unknown_blockchain")
			}
		})
		var res_any
		try {
			res_any = await promiseAny(allpromises)
			// we get res_any='undefined' a lot => means there is no spend
			// log("res_any=",res_any,"for vout=",vout,"txid=",txid,)
		} catch (e) {
			// p-any's AggregateError swallows the individual per-blockchain rejection reasons,
			// so we re-inspect the (already settled) promises to get a clear breakdown of WHY
			// each blockchain rejected, instead of a generic "all blockchains failed"
			var run_txid = txid + "_o" + vout
			const detailed_results = await this._describe_settled_results(allpromises, blockchain_labels)
			var errormess = String([
				"CustomBlockchain: spends(): all blockchains (" + allpromises.length + ") failed for " + run_txid,
				"\n per-blockchain results:", JSON.stringify(detailed_results, null, 2)
			].join(' '))
			console.error(errormess)
			throw new Error(errormess)
		}

		// // show the results from all blockchains
		// function promiseState(p) {
		// 	const t = {};
		// 	return Promise.race([p, t])
		// 	  .then(v => (v === t)? "pending" : "fulfilled", () => "rejected");
		//   }
		// var i= 0
		// allpromises.forEach(async (prom)=>{
		// 	i+=1
		// 	console.error("CustomBlockchain: spends(): allpromises["+i+"] = ",await promiseState(allpromises[0]), "=>",allpromises[0])
		// })

		// retrieved value should be a string (txid) or null if unspent
		// unsure is res_any==undefined is good or a problem ?
		if (res_any === undefined || res_any === null || (is_string(res_any) && res_any.length > 0 && is_hex(res_any))) {
			// all good, means there is no spend
		}
		else if (is_empty_obj(res_any)) { // we get : {} (type [object object])
			// I have no idea why I get those sometimes, when it should be a txid or null according to run-docs
			console.error("CustomBlockchain: spends(): error: is_empty_obj(res_any)=true, res_any=", res_any)
			var runapi_spends = await this.blockchain_runapi.spends(txid, vout)
			console.error("CustomBlockchain: spends() for txid=", txid + "_o" + vout + " :",
				"\n\t runapi_spends=", runapi_spends)
			return null
		}
		else if (res_any && res_any.spend_txid) {
			console.error("CustomBlockchain: spends():  res_any.spend_txid=", res_any.spend_txid)
			return res_any.spend_txid 	// should we use .spend_index too ?
		}
		else {
			throw new Error([
				"CustomBlockchain: spends(): "
				+ "res_any should be an hex string (txid) or null, "
				+ "but got: (" + (typeof res_any) + "):'", res_any, "' (stringified: '", JSON.stringify(res_any), "')"
				+ "for vout=", vout, " & txid=", txid,
			].join(' '))
		}

		if (this.DEBUG) log("promises res_any=", typeof res_any, ": ", res_any)
		if (res_any === null) return null
		return res_any // we return the first successful one
	}
}
